import { useSelector } from 'react-redux';
import { useEffect, useState, useMemo, useRef } from 'react';
import Template from '../Template/Template';
import {
  useGetVendorsQuery,
  useSendBatchEmailMutation
} from '../../store/vendorsApi';
import { useTable, usePagination, useRowSelect } from 'react-table';
import {
  Button,
  TextField,
  Checkbox,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from 'cfa-react-components';
import styles from './VendorEmail.module.scss';
import { toast } from 'react-toastify';

const IndeterminateCheckbox = ({ indeterminate, ...rest }) => {
  const defaultRef = useRef(null);
  const resolvedRef = rest.ref || defaultRef;

  useEffect(() => {
    if (resolvedRef.current) {
      resolvedRef.current.indeterminate = indeterminate;
    }
  }, [resolvedRef, indeterminate]);

  return <Checkbox ref={resolvedRef} {...rest} />;
};

// const pendingHandler = () => 'Sending emails...';
// const successHandler = (response) => {
//   const count =
//     response?.count || response?.emailsSent || response?.vendorsCount || 0;
//   return `Emails sent successfully to ${count} vendor(s)`;
// };
// const requestAndNotify = toastPromise(pendingHandler, successHandler);

const VendorEmail = () => {
  const { data: vendors = [], isLoading } = useGetVendorsQuery();
  const [sendBatchEmail] = useSendBatchEmailMutation();

  const { isAdmin } = useSelector((state) => state.auth);

  const [emailText, setEmailText] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedVendorsForEmail, setSelectedVendorsForEmail] = useState([]);

  const filteredData = useMemo(() => {
    if (!vendors || !Array.isArray(vendors)) return [];
    if (!searchFilter) return vendors;
    return vendors.filter((vendor) =>
      vendor.name?.toLowerCase().includes(searchFilter.toLowerCase())
    );
  }, [vendors, searchFilter]);

  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
        Cell: (props) => {
          return <div className={styles.nameCell}>{props.value}</div>;
        }
      },
      {
        Header: 'Email',
        accessor: 'email',
        Cell: (props) => {
          return <div className={styles.emailCell}>{props.value}</div>;
        }
      }
    ],
    []
  );

  const tableInstance = useTable(
    {
      columns,
      data: filteredData,
      initialState: { pageIndex: 0, pageSize: 50 },
      autoResetPage: false,
      autoResetSelectedRows: false,
      autoResetPageIndex: false
    },
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        {
          id: 'selection',
          Header: ({ getToggleAllPageRowsSelectedProps }) => (
            <div>
              <IndeterminateCheckbox {...getToggleAllPageRowsSelectedProps()} />
            </div>
          ),
          Cell: ({ row }) => (
            <div>
              <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
            </div>
          )
        },
        ...columns
      ]);
    }
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    selectedFlatRows,
    state: { pageIndex, pageSize }
  } = tableInstance;

  const handleSendEmail = () => {
    const vendorsToEmail = selectedFlatRows.map((row) => ({
      id: row.original.id,
      name: row.original.name,
      email: row.original.email
    }));
    setSelectedVendorsForEmail(vendorsToEmail);
    setShowConfirmModal(true);
  };

  const confirmSendEmail = async () => {
    try {
      await sendBatchEmail({
        vendors: selectedVendorsForEmail,
        emailText: emailText
      }).unwrap();

      // Show success message with the actual count we sent
      toast.success(
        `Emails sent successfully to ${selectedVendorsForEmail.length} vendors`
      );

      setShowConfirmModal(false);
      setEmailText('');
    } catch (error) {
      // console.error('Batch email error:', error);
      toast.error(
        'Failed to send emails: ' +
          (error?.data?.message || error?.message || 'Unknown error')
      );
    }
  };

  const renderConfirmModal = () => {
    return (
      <Modal
        size="md"
        show={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
      >
        <ModalHeader>Confirm Email Send</ModalHeader>
        <ModalBody>
          <div className={styles.confirmModalBody}>
            <p>
              You are about to send an email to {selectedVendorsForEmail.length}{' '}
              vendor(s):
            </p>
            <div className={styles.vendorsList}>
              {selectedVendorsForEmail.slice(0, 5).map((vendor) => (
                <div key={vendor.id}>
                  {vendor.name} ({vendor.email})
                </div>
              ))}
              {selectedVendorsForEmail.length > 5 && (
                <div>...and {selectedVendorsForEmail.length - 5} more</div>
              )}
            </div>
            <div className={styles.emailPreview}>
              <strong>Email Content:</strong>
              <div className={styles.emailText}>{emailText}</div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button onClick={() => setShowConfirmModal(false)}>Cancel</Button>
          <Button onClick={confirmSendEmail}>Send Emails</Button>
        </ModalFooter>
      </Modal>
    );
  };

  const canSendEmail =
    selectedFlatRows.length > 0 && emailText.trim().length > 0;

  const tableContent = !isLoading ? (
    <div className={styles.container}>
      <h2>Email Vendors</h2>

      <div className={styles.searchSection}>
        <TextField
          label="Search by Name"
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          placeholder="Enter vendor name..."
          className={styles.searchInput}
        />
      </div>

      <div className={styles.tableSection}>
        <h3 className={styles.sectionHeading}>Vendors</h3>
        <div className={styles.tableContainer}>
          <table {...getTableProps()} className={styles.vendorTable}>
            <thead>
              {headerGroups.map((headerGroup, i) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={i}>
                  {headerGroup.headers.map((column) => (
                    <th
                      {...column.getHeaderProps()}
                      className={styles.tableHeader}
                      key={column.id}
                    >
                      {column.render('Header')}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={row.id}>
                    {row.cells.map((cell) => {
                      return (
                        <td
                          {...cell.getCellProps()}
                          className={styles.tableCell}
                          key={cell.column.id}
                        >
                          {cell.render('Cell')}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className={styles.pagination}>
          <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
            {'<<'}
          </button>{' '}
          <button onClick={() => previousPage()} disabled={!canPreviousPage}>
            {'<'}
          </button>{' '}
          <button onClick={() => nextPage()} disabled={!canNextPage}>
            {'>'}
          </button>{' '}
          <button
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
          >
            {'>>'}
          </button>{' '}
          <span>
            Page{' '}
            <strong>
              {pageIndex + 1} of {pageOptions.length}
            </strong>{' '}
          </span>
          <span>
            | Go to page:{' '}
            <input
              type="number"
              defaultValue={pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                gotoPage(page);
              }}
              style={{ width: '100px' }}
            />
          </span>{' '}
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
            }}
          >
            {[10, 20, 30, 40, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.selectedCount}>
          {selectedFlatRows.length} vendor(s) selected
        </div>
      </div>

      <div className={styles.emailSection}>
        <h3 className={styles.sectionHeading}>Email Message</h3>
        <textarea
          id="emailText"
          className={styles.emailTextarea}
          value={emailText}
          onChange={(e) => setEmailText(e.target.value)}
          placeholder="Enter the email message to send to selected vendors..."
          rows={10}
        />
      </div>

      <div className={styles.actionSection}>
        <Button
          onClick={handleSendEmail}
          disabled={!canSendEmail}
          className={styles.sendButton}
        >
          Send Email to Selected Vendors
        </Button>
      </div>

      {renderConfirmModal()}
    </div>
  ) : (
    <div className={styles.loading}>Loading vendors...</div>
  );

  const content = isAdmin ? (
    tableContent
  ) : (
    <div className={`${styles.unauthorized} ${styles.error}`}>
      You are not authorized to view this page
    </div>
  );

  return <Template>{content}</Template>;
};

export default VendorEmail;
